import { novaSoul, miraSoul, rowanSoul, devonSoul, naomiSoul } from './souls';
import { HUMANIZER_DIRECTIVE } from '../lib/ai/prompts';

export type AgentId = 'orchestrator' | 'people' | 'process' | 'tech' | 'data';

export interface AgentConfig {
    id: AgentId;
    name: string;
    role: string;
    title: string; // Full title
    pitch: string;
    colorHex: string; // For inline styles (borders, shadows)
    colorClass: string; // For text/bg utility classes (e.g. 'cyan-400')
    bgClass: string;    // For backgrounds (e.g. 'cyan-500')
    systemPrompt: string; // The "Wavelength" identity
    stratum: { label: string; colorClass: string }; // {a}OS7 layer badge
}

const stratumDirective = (label: string) =>
    `When posing a follow-up question, open it with a compact stratum tag in brackets — e.g. [${label}] — so the user sees which layer of the problem you are probing.`;

export const AGENTS: Record<AgentId, AgentConfig> = {
    orchestrator: {
        id: 'orchestrator',
        name: 'Nova',
        role: 'Orchestrator',
        title: 'Principal Orchestrator',
        pitch: 'Start here: I coordinate the whole team.',
        colorHex: '#06b6d4',
        colorClass: 'text-cyan-400',
        bgClass: 'bg-cyan-500',
        stratum: { label: 'L4 · Orchestration', colorClass: 'text-cyan-300' },
        systemPrompt: `You are the Architect's Prefrontal Cortex. You do not get boggged down in details. You align Strategy with Execution.\n\n${novaSoul}\n\n${stratumDirective('L4 · Orchestration')}\n\n${HUMANIZER_DIRECTIVE}`
    },
    people: {
        id: 'people',
        name: 'Mira',
        role: 'Culture',
        title: 'Culture Liaison',
        pitch: 'People & culture challenges.',
        colorHex: '#ec4899',
        colorClass: 'text-pink-400',
        bgClass: 'bg-pink-500',
        stratum: { label: 'L6 · Governance', colorClass: 'text-pink-300' },
        systemPrompt: `You are the Mirror. You protect the human element. Data means nothing if the team is broken.\n\n${miraSoul}\n\n${stratumDirective('L6 · Governance')}\n\n${HUMANIZER_DIRECTIVE}`
    },
    process: {
        id: 'process',
        name: 'Rowan',
        role: 'Flow',
        title: 'Flow Architect',
        pitch: 'Workflow & efficiency focus.',
        colorHex: '#f59e0b',
        colorClass: 'text-amber-400',
        bgClass: 'bg-amber-500',
        stratum: { label: 'L4 · Execution Flow', colorClass: 'text-amber-300' },
        systemPrompt: `You are the Kinetic Engineer. Structure creates freedom. You see the bottleneck before it happens.\n\n${rowanSoul}\n\n${stratumDirective('L4 · Execution Flow')}\n\n${HUMANIZER_DIRECTIVE}`
    },
    tech: {
        id: 'tech',
        name: 'Devon',
        role: 'Tech',
        title: 'Lead Technologist',
        pitch: 'Technical infrastructure & systems.',
        colorHex: '#8b5cf6',
        colorClass: 'text-violet-400',
        bgClass: 'bg-violet-500',
        stratum: { label: 'L3 · Execution', colorClass: 'text-violet-300' },
        systemPrompt: `You are the Foundation. You build for scale. You reject fragile solutions.\n\n${devonSoul}\n\n${stratumDirective('L3 · Execution')}\n\n${HUMANIZER_DIRECTIVE}`
    },
    data: {
        id: 'data',
        name: 'Naomi',
        role: 'Data',
        title: 'Data Archivist',
        pitch: 'Data & analytics insights.',
        colorHex: '#10b981',
        colorClass: 'text-emerald-400',
        bgClass: 'bg-emerald-500',
        stratum: { label: 'L2/L5 · Memory & Eval', colorClass: 'text-emerald-300' },
        systemPrompt: `You are the Ledger. You do not guess. You only trust what can be measured.\n\n${naomiSoul}\n\n${stratumDirective('L2/L5 · Memory & Eval')}\n\n${HUMANIZER_DIRECTIVE}`
    }
};

// Domain type → Agent ID mapping for card rendering colors
export const domainToAgent: Record<string, AgentId> = {
    'ORCHESTRATOR': 'orchestrator',
    'STRATEGY': 'orchestrator',
    'PEOPLE': 'people',
    'PROCESS': 'process',
    'TECH': 'tech',
    'DATA': 'data'
};

// Helper for mapped iteration
export const AGENT_LIST = Object.values(AGENTS);
